import React, { useState, useEffect } from 'react';
import apiService from '../services/api';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import useRealTimeData from '../hooks/useRealTimeData';
import { useRealTime } from '../context/RealTimeContext';
import { toast } from 'react-hot-toast';
import { getSupplierInvoicesDetailed } from '../services/api';

const BusinessIntelligence = () => {
  const { subscribe, unsubscribe } = useRealTime();
  const [activeTab, setActiveTab] = useState('sales-executive');
  const [dateRange, setDateRange] = useState({
    startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });
  const [locationGroupBy, setLocationGroupBy] = useState('state');
  const [refreshingAll, setRefreshingAll] = useState(false);

  // Payment modal state
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [paymentData, setPaymentData] = useState({
    amount: '',
    paymentDate: new Date().toISOString().split('T')[0],
    paymentMethod: 'bank_transfer',
    referenceNumber: '',
    notes: ''
  });
  const [recordingPayment, setRecordingPayment] = useState(false);

  // Invoice view modal state
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [selectedSupplierForInvoices, setSelectedSupplierForInvoices] = useState(null);
  const [supplierInvoices, setSupplierInvoices] = useState(null);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [paymentAmounts, setPaymentAmounts] = useState({});
  const [paymentMethods, setPaymentMethods] = useState({});
  const [recordingPayments, setRecordingPayments] = useState({});

  // Real-time data hooks for each report type
  const {
    data: salesBySalesExecutive,
    loading: salesExecutiveLoading,
    error: salesExecutiveError,
    lastRefresh: salesExecutiveLastRefresh,
    refresh: refreshSalesExecutive
  } = useRealTimeData(
    () => fetchReportData('sales-executive'),
    30000, // 30 seconds
    true, // Enable notifications
    [dateRange]
  );

  const {
    data: salesByCustomerType,
    loading: customerTypeLoading,
    error: customerTypeError,
    lastRefresh: customerTypeLastRefresh,
    refresh: refreshCustomerType
  } = useRealTimeData(
    () => fetchReportData('customer-type'),
    30000,
    true,
    [dateRange]
  );

  const {
    data: salesByLocation,
    loading: locationLoading,
    error: locationError,
    lastRefresh: locationLastRefresh,
    refresh: refreshLocation
  } = useRealTimeData(
    () => fetchReportData('location'),
    30000,
    true,
    [dateRange, locationGroupBy]
  );

  const {
    data: salesByCategory,
    loading: categoryLoading,
    error: categoryError,
    lastRefresh: categoryLastRefresh,
    refresh: refreshCategory
  } = useRealTimeData(
    () => fetchReportData('category'),
    30000,
    true,
    [dateRange]
  );

  const {
    data: supplierManagement,
    loading: supplierLoading,
    error: supplierError,
    lastRefresh: supplierLastRefresh,
    refresh: refreshSupplier
  } = useRealTimeData(
    () => fetchReportData('supplier-management'),
    30000,
    true,
    [dateRange]
  );

  const {
    data: salesManagement,
    loading: salesManagementLoading,
    error: salesManagementError,
    lastRefresh: salesManagementLastRefresh,
    refresh: refreshSalesManagement
  } = useRealTimeData(
    () => fetchReportData('sales-management'),
    30000,
    true,
    [dateRange]
  );

  const {
    data: profitAndLoss,
    loading: profitAndLossLoading,
    error: profitAndLossError,
    lastRefresh: profitAndLossLastRefresh,
    refresh: refreshProfitAndLoss
  } = useRealTimeData(
    () => fetchReportData('profit-and-loss'),
    30000,
    true,
    [dateRange]
  );

  const fetchReportData = async (reportType) => {
    try {
      let endpoint = '';
      let params = `?startDate=${dateRange.startDate}&endDate=${dateRange.endDate}`;

      switch (reportType) {
        case 'sales-executive':
          endpoint = `/reports/sales-by-sales-executive${params}`;
          break;
        case 'customer-type':
          endpoint = `/reports/sales-by-customer-type${params}`;
          break;
        case 'location':
          endpoint = `/reports/sales-by-location${params}&groupBy=${locationGroupBy}`;
          break;
        case 'category':
          endpoint = `/reports/sales-by-product-category${params}`;
          break;
        case 'supplier-management':
          endpoint = `/reports/supplier-management${params}`;
          break;
        case 'sales-management':
          endpoint = `/reports/sales-management${params}`;
          break;
        case 'profit-and-loss':
          endpoint = `/reports/profit-and-loss${params}`;
          break;
        default:
          throw new Error('Invalid report type');
      }

      console.log(`🔄 Fetching ${reportType} report from: ${endpoint}`);
      console.log(`📅 Date range: ${dateRange.startDate} to ${dateRange.endDate}`);

      const startTime = Date.now();
      const data = await apiService.get(endpoint);
      const endTime = Date.now();

      console.log(`✅ ${reportType} report data received in ${endTime - startTime}ms:`, data);
      console.log(`📊 ${reportType} report summary:`, {
        totalSalesExecutives: data.totalSalesExecutives,
        totalRevenue: data.totalRevenue,
        totalOrders: data.totalOrders,
        salesBySalesman: data.salesBySalesman?.length || 0,
        totalSuppliers: data.summary?.totalSuppliers,
        totalInvoiceValue: data.summary?.totalInvoiceValue,
        totalPendingPayments: data.summary?.totalPendingPayments
      });

      return data;
    } catch (err) {
      console.error(`❌ Error fetching ${reportType} report:`, err);
      console.error(`❌ Error details:`, {
        message: err.message,
        status: err.status,
        response: err.response
      });
      throw new Error(`Failed to load ${reportType} report: ${err.message}`);
    }
  };

  const handleRefreshAllReports = async () => {
    try {
      setRefreshingAll(true);
      await apiService.refreshAllReports();
      toast.success('All reports refreshed successfully');

      // Refresh all individual reports
      await Promise.all([
        refreshSalesExecutive(),
        refreshCustomerType(),
        refreshLocation(),
        refreshCategory(),
        refreshSupplier(),
        refreshSalesManagement(),
        refreshProfitAndLoss()
      ]);
    } catch (error) {
      console.error('Error refreshing all reports:', error);
      toast.error('Failed to refresh reports');
    } finally {
      setRefreshingAll(false);
    }
  };

  // Get current loading and error states based on active tab
  const getCurrentLoadingState = () => {
    switch (activeTab) {
      case 'sales-executive':
        return salesExecutiveLoading;
      case 'customer-type':
        return customerTypeLoading;
      case 'location':
        return locationLoading;
      case 'category':
        return categoryLoading;
      case 'supplier-management':
        return supplierLoading;
      case 'sales-management':
        return salesManagementLoading;
      case 'profit-and-loss':
        return profitAndLossLoading;
      default:
        return false;
    }
  };

  const getCurrentErrorState = () => {
    switch (activeTab) {
      case 'sales-executive':
        return salesExecutiveError;
      case 'customer-type':
        return customerTypeError;
      case 'location':
        return locationError;
      case 'category':
        return categoryError;
      case 'supplier-management':
        return supplierError;
      case 'sales-management':
        return salesManagementError;
      case 'profit-and-loss':
        return profitAndLossError;
      default:
        return null;
    }
  };

  const getCurrentLastRefresh = () => {
    switch (activeTab) {
      case 'sales-executive':
        return salesExecutiveLastRefresh;
      case 'customer-type':
        return customerTypeLastRefresh;
      case 'location':
        return locationLastRefresh;
      case 'category':
        return categoryLastRefresh;
      case 'supplier-management':
        return supplierLastRefresh;
      case 'sales-management':
        return salesManagementLastRefresh;
      case 'profit-and-loss':
        return profitAndLossLastRefresh;
      default:
        return new Date();
    }
  };

  // Subscribe to real-time updates
  useEffect(() => {
    const handleOrderUpdate = (data) => {
      console.log('🔄 Business Intelligence received order update:', data);
      console.log('🔄 Current active tab:', activeTab);
      console.log('🔄 Triggering refresh for all reports...');

      // Show toast notification
      toast.success('New order activity detected! Refreshing reports...');

      // Refresh all reports when order is approved or invoice is created
      console.log('🔄 Refreshing sales executive report...');
      refreshSalesExecutive();

      console.log('🔄 Refreshing customer type report...');
      refreshCustomerType();

      console.log('🔄 Refreshing location report...');
      refreshLocation();

      console.log('🔄 Refreshing category report...');
      refreshCategory();

      console.log('🔄 Refreshing supplier management report...');
      refreshSupplier();

      console.log('🔄 Refreshing sales management report...');
      refreshSalesManagement();

      console.log('🔄 Refreshing profit and loss report...');
      refreshProfitAndLoss();

      console.log('✅ All report refreshes triggered');
    };

    console.log('📡 Business Intelligence subscribing to real-time events...');

    // Subscribe to order and invoice events
    subscribe('orderApproved', handleOrderUpdate, 'BusinessIntelligence');
    subscribe('invoiceCreated', handleOrderUpdate, 'BusinessIntelligence');
    subscribe('deliveryOrderCreated', handleOrderUpdate, 'BusinessIntelligence');

    console.log('✅ Business Intelligence subscribed to real-time events');

    // Cleanup subscriptions
    return () => {
      console.log('🧹 Business Intelligence unsubscribing from real-time events...');
      unsubscribe('orderApproved', 'BusinessIntelligence');
      unsubscribe('invoiceCreated', 'BusinessIntelligence');
      unsubscribe('deliveryOrderCreated', 'BusinessIntelligence');
      console.log('✅ Business Intelligence unsubscribed from real-time events');
    };
  }, [subscribe, unsubscribe, refreshSalesExecutive, refreshCustomerType, refreshLocation, refreshCategory, refreshSupplier, activeTab]);

  const refreshCurrentReport = () => {
    switch (activeTab) {
      case 'sales-executive':
        refreshSalesExecutive();
        break;
      case 'customer-type':
        refreshCustomerType();
        break;
      case 'location':
        refreshLocation();
        break;
      case 'category':
        refreshCategory();
        break;
      case 'supplier-management':
        refreshSupplier();
        break;
      case 'sales-management':
        refreshSalesManagement();
        break;
      case 'profit-and-loss':
        refreshProfitAndLoss();
        break;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };

  // Payment handling functions
  const handleRecordPayment = (supplier) => {
    setSelectedSupplier(supplier);
    setPaymentData({
      amount: '',
      paymentDate: new Date().toISOString().split('T')[0],
      paymentMethod: 'bank_transfer',
      referenceNumber: '',
      notes: ''
    });
    setShowPaymentModal(true);
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();

    if (!selectedSupplier || !paymentData.amount) {
      toast.error('Please fill in all required fields');
      return;
    }

    setRecordingPayment(true);

    try {
      // For now, we'll record payment against the first invoice
      // In a real implementation, you'd want to select specific invoices
      const response = await apiService.post(`/supplier-invoices/${selectedSupplier.recentInvoices?.[0]?.id}/payments`, paymentData);

      toast.success('Payment recorded successfully');
      setShowPaymentModal(false);
      setSelectedSupplier(null);

      // Refresh supplier data
      refreshSupplier();

    } catch (error) {
      console.error('Error recording payment:', error);
      toast.error('Failed to record payment');
    } finally {
      setRecordingPayment(false);
    }
  };

  const closePaymentModal = () => {
    setShowPaymentModal(false);
    setSelectedSupplier(null);
    setPaymentData({
      amount: '',
      paymentDate: new Date().toISOString().split('T')[0],
      paymentMethod: 'bank_transfer',
      referenceNumber: '',
      notes: ''
    });
  };

  // Invoice view handling functions
  const handleViewInvoices = async (supplier) => {
    setSelectedSupplierForInvoices(supplier);
    setLoadingInvoices(true);
    setShowInvoiceModal(true);

    try {
      const response = await getSupplierInvoicesDetailed(supplier.supplierId, {
        startDate: dateRange.startDate,
        endDate: dateRange.endDate
      });
      setSupplierInvoices(response);
    } catch (error) {
      console.error('Error fetching supplier invoices:', error);
      toast.error('Failed to fetch supplier invoices');
    } finally {
      setLoadingInvoices(false);
    }
  };

  const closeInvoiceModal = () => {
    setShowInvoiceModal(false);
    setSelectedSupplierForInvoices(null);
    setSupplierInvoices(null);
  };

  const handleRecordPaymentForInvoice = (invoice) => {
    setSelectedSupplier(selectedSupplierForInvoices);
    setPaymentData({
      amount: invoice.balanceAmount || invoice.totalAmount,
      paymentDate: new Date().toISOString().split('T')[0],
      paymentMethod: 'bank_transfer',
      referenceNumber: '',
      notes: `Payment for invoice ${invoice.invoiceNumber}`
    });
    setShowPaymentModal(true);
    setShowInvoiceModal(false);
  };

  const handleInlinePaymentChange = (invoiceId, amount) => {
    setPaymentAmounts(prev => ({
      ...prev,
      [invoiceId]: amount
    }));
  };

  const handlePaymentMethodChange = (invoiceId, method) => {
    setPaymentMethods(prev => ({
      ...prev,
      [invoiceId]: method
    }));
  };

  const handleInlinePaymentSubmit = async (invoice) => {
    const amount = paymentAmounts[invoice.id];
    if (!amount || parseFloat(amount) <= 0) {
      toast.error('Please enter a valid payment amount');
      return;
    }

    if (parseFloat(amount) > invoice.balanceAmount) {
      toast.error('Payment amount cannot exceed remaining balance');
      return;
    }

    setRecordingPayments(prev => ({ ...prev, [invoice.id]: true }));

    try {
      const paymentData = {
        amount: parseFloat(amount),
        paymentDate: new Date().toISOString().split('T')[0],
        paymentMethod: paymentMethods[invoice.id] || 'bank_transfer',
        referenceNumber: '',
        notes: `Payment for invoice ${invoice.invoiceNumber}`
      };

      await apiService.post(`/supplier-invoices/${invoice.id}/payments`, paymentData);

      toast.success('Payment recorded successfully');

      // Clear the payment amount and method
      setPaymentAmounts(prev => {
        const newAmounts = { ...prev };
        delete newAmounts[invoice.id];
        return newAmounts;
      });
      setPaymentMethods(prev => {
        const newMethods = { ...prev };
        delete newMethods[invoice.id];
        return newMethods;
      });

      // Refresh the invoice data
      const response = await getSupplierInvoicesDetailed(selectedSupplierForInvoices.supplierId, {
        startDate: dateRange.startDate,
        endDate: dateRange.endDate
      });
      setSupplierInvoices(response);

      // Refresh supplier management data
      refreshSupplier();

    } catch (error) {
      console.error('Error recording payment:', error);
      toast.error('Failed to record payment');
    } finally {
      setRecordingPayments(prev => ({ ...prev, [invoice.id]: false }));
    }
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-IN').format(num);
  };

  const renderSalesBySalesExecutive = () => {
    if (!salesBySalesExecutive) return null;

    return (
      <div className="space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500">Total Sales Executives</h3>
            <p className="text-2xl font-bold text-blue-600">{salesBySalesExecutive.totalSalesExecutives}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500">Total Revenue</h3>
            <p className="text-2xl font-bold text-green-600">{formatCurrency(salesBySalesExecutive.totalRevenue)}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500">Total Orders</h3>
            <p className="text-2xl font-bold text-purple-600">{formatNumber(salesBySalesExecutive.totalOrders)}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500">Average Revenue/SE</h3>
            <p className="text-2xl font-bold text-orange-600">
              {formatCurrency(salesBySalesExecutive.totalSalesExecutives > 0 ? salesBySalesExecutive.totalRevenue / salesBySalesExecutive.totalSalesExecutives : 0)}
            </p>
          </div>
        </div>

        {/* Sales Executive Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Sales Performance by Sales Executive</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sales Executive</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Assigned Customers</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Active Customers</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Orders</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Revenue</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Avg Order Value</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Items Sold</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {salesBySalesExecutive.salesBySalesman.map((se, index) => (
                  <tr key={se.salesExecutiveId} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{se.salesExecutiveName}</div>
                        <div className="text-sm text-gray-500">{se.email}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{se.assignedCustomers}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{se.activeCustomers}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatNumber(se.totalOrders)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600">{formatCurrency(se.totalRevenue)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatCurrency(se.averageOrderValue)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatNumber(se.totalItems)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  const renderSalesByCustomerType = () => {
    if (!salesByCustomerType) return null;

    return (
      <div className="space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500">Total Revenue</h3>
            <p className="text-2xl font-bold text-green-600">{formatCurrency(salesByCustomerType.totalRevenue)}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500">Total Customers</h3>
            <p className="text-2xl font-bold text-blue-600">{formatNumber(salesByCustomerType.totalCustomers)}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500">Total Orders</h3>
            <p className="text-2xl font-bold text-purple-600">{formatNumber(salesByCustomerType.totalOrders)}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500">Customer Types</h3>
            <p className="text-2xl font-bold text-orange-600">{salesByCustomerType.salesByCustomerType.length}</p>
          </div>
        </div>

        {/* Customer Type Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {salesByCustomerType.salesByCustomerType.map((typeData) => (
            <div key={typeData.customerType} className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 capitalize">{typeData.customerType}</h3>
                <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full">
                  {typeData.totalCustomers} customers
                </span>
              </div>

              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Revenue:</span>
                  <span className="text-sm font-medium text-green-600">{formatCurrency(typeData.totalRevenue)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Orders:</span>
                  <span className="text-sm font-medium">{formatNumber(typeData.totalOrders)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Avg Order Value:</span>
                  <span className="text-sm font-medium">{formatCurrency(typeData.averageOrderValue)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-500">Items Sold:</span>
                  <span className="text-sm font-medium">{formatNumber(typeData.totalItems)}</span>
                </div>
              </div>

              {/* Top Customers */}
              <div className="mt-4 pt-4 border-t border-gray-200">
                <h4 className="text-sm font-medium text-gray-900 mb-2">Top Customers</h4>
                <div className="space-y-1">
                  {typeData.customers.slice(0, 3).map((customer) => (
                    <div key={customer.id} className="flex justify-between text-xs">
                      <span className="text-gray-600 truncate">{customer.businessName || customer.name}</span>
                      <span className="text-gray-900 font-medium">{formatCurrency(customer.totalRevenue)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderSalesByLocation = () => {
    if (!salesByLocation) return null;

    return (
      <div className="space-y-6">
        {/* Controls */}
        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex items-center space-x-4">
            <label className="text-sm font-medium text-gray-700">Group By:</label>
            <select
              value={locationGroupBy}
              onChange={(e) => setLocationGroupBy(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm"
            >
              <option value="country">Country</option>
              <option value="state">State</option>
              <option value="district">District</option>
              <option value="city">City</option>
            </select>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500">Total Revenue</h3>
            <p className="text-2xl font-bold text-green-600">{formatCurrency(salesByLocation.totalRevenue)}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500">Total Orders</h3>
            <p className="text-2xl font-bold text-purple-600">{formatNumber(salesByLocation.totalOrders)}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500">Total Customers</h3>
            <p className="text-2xl font-bold text-blue-600">{formatNumber(salesByLocation.totalCustomers)}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500">Locations</h3>
            <p className="text-2xl font-bold text-orange-600">{salesByLocation.salesByLocation.length}</p>
          </div>
        </div>

        {/* Location Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Sales by Location ({locationGroupBy})</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Orders</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Revenue</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customers</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Avg Order Value</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Items Sold</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {salesByLocation.salesByLocation.map((location, index) => (
                  <tr key={location.locationKey} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{location.locationName}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatNumber(location.totalOrders)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-600">{formatCurrency(location.totalRevenue)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{location.totalCustomers}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatCurrency(location.averageOrderValue)}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatNumber(location.totalItems)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  const renderSalesByCategory = () => {
    if (!salesByCategory) return null;

    return (
      <div className="space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500">Total Categories</h3>
            <p className="text-2xl font-bold text-blue-600">{salesByCategory.totalCategories}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500">Total Revenue</h3>
            <p className="text-2xl font-bold text-green-600">{formatCurrency(salesByCategory.totalRevenue)}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500">Total Orders</h3>
            <p className="text-2xl font-bold text-purple-600">{formatNumber(salesByCategory.totalOrders)}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500">Average Revenue/Category</h3>
            <p className="text-2xl font-bold text-orange-600">
              {formatCurrency(salesByCategory.totalCategories > 0 ? salesByCategory.totalRevenue / salesByCategory.totalCategories : 0)}
            </p>
          </div>
        </div>

        {/* Category Performance Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Category Performance</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Revenue</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Orders</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Items Sold</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Avg Order Value</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Top Product</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {salesByCategory.salesByCategory.map((category, index) => (
                  <tr key={category.category} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{category.category}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{formatCurrency(category.totalRevenue)}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{formatNumber(category.totalOrders)}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{formatNumber(category.totalItems)}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{formatCurrency(category.averageOrderValue)}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{category.topProduct}</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  const renderSupplierManagement = () => {
    if (!supplierManagement) {
      console.log('❌ Supplier management data is null');
      return (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-center">
            <p className="text-gray-500">No supplier management data available</p>
          </div>
        </div>
      );
    }

    console.log('📊 Supplier Management Data:', supplierManagement);

    return (
      <div className="space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500">Total Suppliers</h3>
            <p className="text-2xl font-bold text-blue-600">{supplierManagement.summary?.totalSuppliers || 0}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500">Total Invoice Value (Inc. Tax)</h3>
            <p className="text-2xl font-bold text-blue-600">{formatCurrency(supplierManagement.summary?.totalInvoiceValue || 0)}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500">Pending Payments</h3>
            <p className="text-2xl font-bold text-red-600">{formatCurrency(supplierManagement.summary?.totalPendingPayments || 0)}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500">Input Tax</h3>
            <p className="text-2xl font-bold text-purple-600">{formatCurrency(supplierManagement.summary?.totalInputTax || 0)}</p>
          </div>
        </div>

        {/* Supplier Performance Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Supplier Performance</h3>
          </div>
          <div className="overflow-x-auto">
            {supplierManagement.supplierReports && supplierManagement.supplierReports.length > 0 ? (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Supplier</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Invoice Value (Inc. Tax)</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Paid Amount</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Pending</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Payment %</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Input Tax</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {supplierManagement.supplierReports.map((supplier, index) => (
                    <tr key={supplier.supplierId || index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{supplier.supplierName || 'N/A'}</div>
                        <div className="text-sm text-gray-500">{supplier.supplierCode || 'N/A'}</div>
                        <div className="text-xs text-gray-400">{supplier.totalInvoices || 0} invoices</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{formatCurrency(supplier.totalInvoiceValueWithTax || supplier.totalInvoiceAmount || 0)}</div>
                        <div className="text-xs text-gray-500">
                          Base: {formatCurrency(supplier.totalInvoiceAmount || 0)} | Tax: {formatCurrency(supplier.totalTaxAmount || 0)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{formatCurrency(supplier.totalPaidAmount || 0)}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{formatCurrency(supplier.pendingAmount || 0)}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{((supplier.paymentPercentage || 0)).toFixed(1)}%</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{formatCurrency(supplier.totalTaxAmount || 0)}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${(supplier.paymentPercentage || 0) >= 90
                            ? 'bg-green-100 text-green-800'
                            : (supplier.paymentPercentage || 0) >= 70
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-red-100 text-red-800'
                          }`}>
                          {(supplier.paymentPercentage || 0) >= 90 ? 'Good' : (supplier.paymentPercentage || 0) >= 70 ? 'Fair' : 'Poor'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex flex-col space-y-2">
                          <button
                            onClick={() => handleViewInvoices(supplier)}
                            className="text-blue-600 hover:text-blue-900 text-sm font-medium"
                          >
                            View Invoices
                          </button>
                          <button
                            onClick={() => handleRecordPayment(supplier)}
                            className="text-indigo-600 hover:text-indigo-900 text-sm font-medium"
                          >
                            Record Payment
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="px-6 py-8 text-center">
                <p className="text-gray-500">No supplier data available for the selected date range</p>
              </div>
            )}
          </div>
        </div>

        {/* Tax Breakdown */}
        {supplierManagement.overallStats && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Tax Breakdown</h3>
              <div className="space-y-3">
                {/* Calculate actual tax breakdown from supplier reports */}
                {(() => {
                  // Aggregate tax breakdown from all supplier reports
                  const aggregatedTaxBreakdown = {
                    gst5: 0,
                    gst12: 0,
                    gst18: 0,
                    gst28: 0,
                    igst5: 0,
                    igst12: 0,
                    igst18: 0,
                    igst28: 0,
                    exempt: 0,
                    other: 0
                  };

                  // Sum up tax breakdown from all suppliers
                  if (supplierManagement.supplierReports) {
                    supplierManagement.supplierReports.forEach(supplier => {
                      if (supplier.taxBreakdown) {
                        aggregatedTaxBreakdown.gst5 += supplier.taxBreakdown.gst5 || 0;
                        aggregatedTaxBreakdown.gst12 += supplier.taxBreakdown.gst12 || 0;
                        aggregatedTaxBreakdown.gst18 += supplier.taxBreakdown.gst18 || 0;
                        aggregatedTaxBreakdown.gst28 += supplier.taxBreakdown.gst28 || 0;
                        aggregatedTaxBreakdown.igst5 += supplier.taxBreakdown.igst5 || 0;
                        aggregatedTaxBreakdown.igst12 += supplier.taxBreakdown.igst12 || 0;
                        aggregatedTaxBreakdown.igst18 += supplier.taxBreakdown.igst18 || 0;
                        aggregatedTaxBreakdown.igst28 += supplier.taxBreakdown.igst28 || 0;
                        aggregatedTaxBreakdown.exempt += supplier.taxBreakdown.exempt || 0;
                        aggregatedTaxBreakdown.other += supplier.taxBreakdown.other || 0;
                      }
                    });
                  }

                  return (
                    <>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">GST (5%)</span>
                        <span className="text-sm font-medium">{formatCurrency(aggregatedTaxBreakdown.gst5)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">GST (12%)</span>
                        <span className="text-sm font-medium">{formatCurrency(aggregatedTaxBreakdown.gst12)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">GST (18%)</span>
                        <span className="text-sm font-medium">{formatCurrency(aggregatedTaxBreakdown.gst18)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-600">GST (28%)</span>
                        <span className="text-sm font-medium">{formatCurrency(aggregatedTaxBreakdown.gst28)}</span>
                      </div>
                      <div className="flex justify-between border-t pt-2">
                        <span className="text-sm font-medium text-gray-900">Total GST</span>
                        <span className="text-sm font-medium">{formatCurrency(supplierManagement.overallStats.totalGST || 0)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm font-medium text-gray-900">Total IGST</span>
                        <span className="text-sm font-medium">{formatCurrency(supplierManagement.overallStats.totalIGST || 0)}</span>
                      </div>
                    </>
                  );
                })()}
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Payment Performance</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Average Payment Efficiency</span>
                  <span className="text-sm font-medium">{(supplierManagement.overallStats.averagePaymentEfficiency || 0).toFixed(1)}%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Suppliers with Overdue</span>
                  <span className="text-sm font-medium">{supplierManagement.overallStats.suppliersWithOverduePayments || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">At Credit Limit</span>
                  <span className="text-sm font-medium">{supplierManagement.overallStats.suppliersAtCreditLimit || 0}</span>
                </div>
              </div>
            </div>
          </div>
        )}


      </div>
    );
  };

  const renderSalesManagement = () => {
    if (!salesManagement) {
      console.log('❌ Sales management data is null');
      return (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-center">
            <p className="text-gray-500">No sales management data available</p>
          </div>
        </div>
      );
    }

    console.log('📊 Sales Management Data:', salesManagement);

    return (
      <div className="space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500">Total Customers</h3>
            <p className="text-2xl font-bold text-blue-600">{salesManagement.summary?.totalCustomers || 0}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500">Total Sales Value (Inc. Tax)</h3>
            <p className="text-2xl font-bold text-green-600">{formatCurrency(salesManagement.summary?.totalSalesValue || 0)}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500">Total Payments Collected</h3>
            <p className="text-2xl font-bold text-purple-600">{formatCurrency(salesManagement.summary?.totalPaymentsCollected || 0)}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500">Overdue Payments</h3>
            <p className="text-2xl font-bold text-red-600">{formatCurrency(salesManagement.summary?.totalOverduePayments || 0)}</p>
          </div>
        </div>

        {/* Customer Performance Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Customer Performance</h3>
          </div>
          <div className="overflow-x-auto">
            {salesManagement.customerReports && salesManagement.customerReports.length > 0 ? (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sales Value (Inc. Tax)</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Payments Collected</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Pending Amount</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Payment %</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Output Tax</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {salesManagement.customerReports.map((customer, index) => (
                    <tr key={customer.customerId || index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{customer.customerName || 'N/A'}</div>
                        <div className="text-sm text-gray-500">{customer.customerCode || 'N/A'}</div>
                        <div className="text-xs text-gray-400">{customer.totalInvoices || 0} invoices</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{formatCurrency(customer.totalSalesValueWithTax || customer.totalSalesValue || 0)}</div>
                        <div className="text-xs text-gray-500">
                          Base: {formatCurrency(customer.totalSalesValue || 0)} | Tax: {formatCurrency(customer.totalOutputTax || 0)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{formatCurrency(customer.totalPaymentsCollected || 0)}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{formatCurrency(customer.pendingAmount || 0)}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{((customer.paymentPercentage || 0)).toFixed(1)}%</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{formatCurrency(customer.totalOutputTax || 0)}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${customer.paymentStatus === 'paid'
                            ? 'bg-green-100 text-green-800'
                            : customer.paymentStatus === 'partial'
                              ? 'bg-yellow-100 text-yellow-800'
                              : customer.paymentStatus === 'overdue'
                                ? 'bg-red-100 text-red-800'
                                : 'bg-gray-100 text-gray-800'
                          }`}>
                          {customer.paymentStatus === 'paid' ? 'Good' :
                            customer.paymentStatus === 'partial' ? 'Fair' :
                              customer.paymentStatus === 'overdue' ? 'Poor' : 'Pending'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="px-6 py-8 text-center">
                <p className="text-gray-500">No customer data available for the selected date range</p>
              </div>
            )}
          </div>
        </div>

        {/* Sales Analytics */}
        {salesManagement.analytics && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Payment Performance</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Average Payment Efficiency</span>
                  <span className="text-sm font-medium">{((salesManagement.analytics.averagePaymentEfficiency || 0)).toFixed(1)}%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Customers with Overdue</span>
                  <span className="text-sm font-medium">{salesManagement.analytics.customersWithOverduePayments || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">At Credit Limit</span>
                  <span className="text-sm font-medium">{salesManagement.analytics.customersAtCreditLimit || 0}</span>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Sales Trends</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Total Orders</span>
                  <span className="text-sm font-medium">{salesManagement.analytics.totalOrders || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Average Order Value</span>
                  <span className="text-sm font-medium">{formatCurrency(salesManagement.analytics.averageOrderValue || 0)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-gray-600">Top Performing Customer</span>
                  <span className="text-sm font-medium">{salesManagement.analytics.topPerformingCustomer || 'N/A'}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Pagination Info */}
        {salesManagement.pagination && (
          <div className="bg-white rounded-lg shadow p-4">
            <div className="text-sm text-gray-600 text-center">
              Showing {salesManagement.pagination.startIndex} to {salesManagement.pagination.endIndex} of {salesManagement.pagination.totalCustomers} customers
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderProfitAndLoss = () => {
    if (!profitAndLoss) {
      console.log('❌ Profit and Loss data is null');
      return (
        <div className="bg-white rounded-lg shadow p-6">
          <div className="text-center">
            <p className="text-gray-500">No profit and loss data available</p>
          </div>
        </div>
      );
    }

    console.log('📊 Profit and Loss Data:', profitAndLoss);

    const formatPercentage = (value) => `${(value || 0).toFixed(2)}%`;
    const formatNumber = (value) => new Intl.NumberFormat('en-IN').format(value || 0);

    return (
      <div className="space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500">Total Revenue</h3>
            <p className="text-2xl font-bold text-green-600">{formatCurrency(profitAndLoss.totalRevenue || 0)}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500">Total Cost of Goods</h3>
            <p className="text-2xl font-bold text-red-600">{formatCurrency(profitAndLoss.totalCostOfGoods || 0)}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500">Gross Profit</h3>
            <p className="text-2xl font-bold text-blue-600">{formatCurrency(profitAndLoss.grossProfit || 0)}</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500">Net Profit</h3>
            <p className="text-2xl font-bold text-purple-600">{formatCurrency(profitAndLoss.netProfit || 0)}</p>
          </div>
        </div>

        {/* Profit and Loss Statement */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900">Profit & Loss Statement</h3>
            <p className="text-sm text-gray-600">For the period: {dateRange.startDate} to {dateRange.endDate}</p>
          </div>

          <div className="p-6">
            {/* Revenue Section */}
            <div className="mb-8">
              <h4 className="text-lg font-semibold text-gray-900 mb-4">Revenue</h4>
              <div className="space-y-3">
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-gray-700">Sales Revenue</span>
                  <span className="font-medium">{formatCurrency(profitAndLoss.salesRevenue || 0)}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-gray-700">Other Income</span>
                  <span className="font-medium">{formatCurrency(profitAndLoss.otherIncome || 0)}</span>
                </div>
                <div className="flex justify-between items-center py-3 bg-green-50 border-b-2 border-green-200">
                  <span className="font-semibold text-green-800">Total Revenue</span>
                  <span className="font-bold text-green-800">{formatCurrency(profitAndLoss.totalRevenue || 0)}</span>
                </div>
              </div>
            </div>

            {/* Cost of Goods Sold Section */}
            <div className="mb-8">
              <h4 className="text-lg font-semibold text-gray-900 mb-4">Cost of Goods Sold</h4>
              <div className="space-y-3">
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-gray-700">Purchase Cost</span>
                  <span className="font-medium">{formatCurrency(profitAndLoss.purchaseCost || 0)}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-gray-700">Freight & Handling</span>
                  <span className="font-medium">{formatCurrency(profitAndLoss.freightCost || 0)}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-gray-700">Import Duties & Taxes</span>
                  <span className="font-medium">{formatCurrency(profitAndLoss.importDuties || 0)}</span>
                </div>
                <div className="flex justify-between items-center py-3 bg-red-50 border-b-2 border-red-200">
                  <span className="font-semibold text-red-800">Total Cost of Goods</span>
                  <span className="font-bold text-red-800">{formatCurrency(profitAndLoss.totalCostOfGoods || 0)}</span>
                </div>
              </div>
            </div>

            {/* Gross Profit */}
            <div className="mb-8">
              <div className="flex justify-between items-center py-4 bg-blue-50 border-b-2 border-blue-200">
                <span className="text-xl font-bold text-blue-800">Gross Profit</span>
                <span className="text-xl font-bold text-blue-800">{formatCurrency(profitAndLoss.grossProfit || 0)}</span>
              </div>
              <div className="text-center mt-2">
                <span className="text-sm text-gray-600">
                  Gross Profit Margin: {formatPercentage(profitAndLoss.grossProfitMargin || 0)}
                </span>
              </div>
            </div>

            {/* Operating Expenses */}
            <div className="mb-8">
              <h4 className="text-lg font-semibold text-gray-900 mb-4">Operating Expenses</h4>
              <div className="space-y-3">
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-gray-700">Employee Salaries</span>
                  <span className="font-medium">{formatCurrency(profitAndLoss.employeeSalaries || 0)}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-gray-700">Rent & Utilities</span>
                  <span className="font-medium">{formatCurrency(profitAndLoss.rentUtilities || 0)}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-gray-700">Marketing & Advertising</span>
                  <span className="font-medium">{formatCurrency(profitAndLoss.marketingExpenses || 0)}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-gray-700">Office Supplies</span>
                  <span className="font-medium">{formatCurrency(profitAndLoss.officeSupplies || 0)}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-gray-700">Insurance</span>
                  <span className="font-medium">{formatCurrency(profitAndLoss.insurance || 0)}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-gray-700">Depreciation</span>
                  <span className="font-medium">{formatCurrency(profitAndLoss.depreciation || 0)}</span>
                </div>
                <div className="flex justify-between items-center py-3 bg-orange-50 border-b-2 border-orange-200">
                  <span className="font-semibold text-orange-800">Total Operating Expenses</span>
                  <span className="font-bold text-orange-800">{formatCurrency(profitAndLoss.totalOperatingExpenses || 0)}</span>
                </div>
              </div>
            </div>

            {/* Operating Profit */}
            <div className="mb-8">
              <div className="flex justify-between items-center py-4 bg-yellow-50 border-b-2 border-yellow-200">
                <span className="text-xl font-bold text-yellow-800">Operating Profit</span>
                <span className="text-xl font-bold text-yellow-800">{formatCurrency(profitAndLoss.operatingProfit || 0)}</span>
              </div>
              <div className="text-center mt-2">
                <span className="text-sm text-gray-600">
                  Operating Margin: {formatPercentage(profitAndLoss.operatingMargin || 0)}
                </span>
              </div>
            </div>

            {/* Other Income/Expenses */}
            <div className="mb-8">
              <h4 className="text-lg font-semibold text-gray-900 mb-4">Other Income & Expenses</h4>
              <div className="space-y-3">
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-gray-700">Interest Income</span>
                  <span className="font-medium text-green-600">{formatCurrency(profitAndLoss.interestIncome || 0)}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-gray-700">Interest Expense</span>
                  <span className="font-medium text-red-600">{formatCurrency(profitAndLoss.interestExpense || 0)}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                  <span className="text-gray-700">Foreign Exchange Gain/Loss</span>
                  <span className={`font-medium ${(profitAndLoss.foreignExchangeGain || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {formatCurrency(profitAndLoss.foreignExchangeGain || 0)}
                  </span>
                </div>
                <div className="flex justify-between items-center py-3 bg-gray-50 border-b-2 border-gray-200">
                  <span className="font-semibold text-gray-800">Net Other Income</span>
                  <span className={`font-bold ${(profitAndLoss.netOtherIncome || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {formatCurrency(profitAndLoss.netOtherIncome || 0)}
                  </span>
                </div>
              </div>
            </div>

            {/* Net Profit */}
            <div className="mb-8">
              <div className="flex justify-between items-center py-6 bg-purple-50 border-b-2 border-purple-200">
                <span className="text-2xl font-bold text-purple-800">Net Profit</span>
                <span className="text-2xl font-bold text-purple-800">{formatCurrency(profitAndLoss.netProfit || 0)}</span>
              </div>
              <div className="text-center mt-2">
                <span className="text-sm text-gray-600">
                  Net Profit Margin: {formatPercentage(profitAndLoss.netProfitMargin || 0)}
                </span>
              </div>
            </div>

            {/* Key Performance Indicators */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-gray-50 p-4 rounded-lg">
                <h5 className="text-sm font-medium text-gray-700 mb-2">Gross Profit Margin</h5>
                <p className="text-2xl font-bold text-blue-600">{formatPercentage(profitAndLoss.grossProfitMargin || 0)}</p>
                <p className="text-xs text-gray-500 mt-1">Revenue - COGS / Revenue</p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <h5 className="text-sm font-medium text-gray-700 mb-2">Operating Margin</h5>
                <p className="text-2xl font-bold text-yellow-600">{formatPercentage(profitAndLoss.operatingMargin || 0)}</p>
                <p className="text-xs text-gray-500 mt-1">Operating Profit / Revenue</p>
              </div>
              <div className="bg-gray-50 p-4 rounded-lg">
                <h5 className="text-sm font-medium text-gray-700 mb-2">Net Profit Margin</h5>
                <p className="text-2xl font-bold text-purple-600">{formatPercentage(profitAndLoss.netProfitMargin || 0)}</p>
                <p className="text-xs text-gray-500 mt-1">Net Profit / Revenue</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="w-full min-h-screen bg-gray-50 py-6 px-4 sm:px-6 lg:px-8">
      <div className="mb-8">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Business Intelligence</h1>
            <p className="mt-2 text-gray-600">Comprehensive sales analytics and performance insights</p>
          </div>

          {/* Real-time Status and Refresh */}
          <div className="flex items-center space-x-4">
            <div className="text-right">
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                <span className="text-sm text-gray-600">Live Updates</span>
              </div>
              <div className="text-xs text-gray-500">
                Last updated: {getCurrentLastRefresh().toLocaleTimeString()}
              </div>
            </div>
            <button
              onClick={refreshCurrentReport}
              disabled={getCurrentLoadingState()}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <svg className={`w-4 h-4 ${getCurrentLoadingState() ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              <span>{getCurrentLoadingState() ? 'Refreshing...' : 'Refresh'}</span>
            </button>
            <button
              onClick={handleRefreshAllReports}
              disabled={refreshingAll}
              className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-opacity-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <svg className={`w-4 h-4 ${refreshingAll ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              <span>{refreshingAll ? 'Refreshing All...' : 'Refresh All Reports'}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Date Range Selector */}
      <div className="bg-white p-4 rounded-lg shadow mb-6">
        <div className="flex items-center space-x-4">
          <label className="text-sm font-medium text-gray-700">Date Range:</label>
          <input
            type="date"
            value={dateRange.startDate}
            onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm"
          />
          <span className="text-gray-500">to</span>
          <input
            type="date"
            value={dateRange.endDate}
            onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm"
          />

          {/* Location Group By Selector (only for location tab) */}
          {activeTab === 'location' && (
            <>
              <span className="text-gray-500">|</span>
              <label className="text-sm font-medium text-gray-700">Group By:</label>
              <select
                value={locationGroupBy}
                onChange={(e) => setLocationGroupBy(e.target.value)}
                className="border border-gray-300 rounded-md px-3 py-2 text-sm"
              >
                <option value="state">State</option>
                <option value="district">District</option>
                <option value="city">City</option>
              </select>
            </>
          )}
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          {[
            { id: 'sales-executive', name: 'Sales by Sales Executive' },
            { id: 'customer-type', name: 'Sales by Customer Type' },
            { id: 'location', name: 'Sales by Location' },
            { id: 'category', name: 'Sales by Product Category' },
            { id: 'supplier-management', name: 'Supplier Management' },
            { id: 'sales-management', name: 'Sales Management' },
            { id: 'profit-and-loss', name: 'Profit & Loss' }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
            >
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      {/* Error Display */}
      {getCurrentErrorState() && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <div className="mt-2 text-sm text-red-700">{getCurrentErrorState()}</div>
            </div>
          </div>
        </div>
      )}

      {/* Loading State */}
      {getCurrentLoadingState() && (
        <div className="flex justify-center items-center h-64">
          <LoadingSpinner size="lg" />
        </div>
      )}

      {/* Report Content */}
      {!getCurrentLoadingState() && !getCurrentErrorState() && (
        <div>
          {activeTab === 'sales-executive' && renderSalesBySalesExecutive()}
          {activeTab === 'customer-type' && renderSalesByCustomerType()}
          {activeTab === 'location' && renderSalesByLocation()}
          {activeTab === 'category' && renderSalesByCategory()}
          {activeTab === 'supplier-management' && renderSupplierManagement()}
          {activeTab === 'sales-management' && renderSalesManagement()}
          {activeTab === 'profit-and-loss' && renderProfitAndLoss()}
        </div>
      )}

      {/* Payment Modal */}
      {showPaymentModal && selectedSupplier && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                Record Payment - {selectedSupplier.supplierName}
              </h3>

              <form onSubmit={handlePaymentSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Amount *</label>
                  <input
                    type="number"
                    step="0.01"
                    required
                    value={paymentData.amount}
                    onChange={(e) => setPaymentData({ ...paymentData, amount: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                    placeholder="Enter payment amount"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Payment Date *</label>
                  <input
                    type="date"
                    required
                    value={paymentData.paymentDate}
                    onChange={(e) => setPaymentData({ ...paymentData, paymentDate: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Payment Method</label>
                  <select
                    value={paymentData.paymentMethod}
                    onChange={(e) => setPaymentData({ ...paymentData, paymentMethod: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  >
                    <option value="bank_transfer">Bank Transfer</option>
                    <option value="cash">Cash</option>
                    <option value="cheque">Cheque</option>
                    <option value="online">Online Payment</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Reference Number</label>
                  <input
                    type="text"
                    value={paymentData.referenceNumber}
                    onChange={(e) => setPaymentData({ ...paymentData, referenceNumber: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                    placeholder="Transaction ID, cheque number, etc."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">Notes</label>
                  <textarea
                    value={paymentData.notes}
                    onChange={(e) => setPaymentData({ ...paymentData, notes: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                    rows="3"
                    placeholder="Additional notes..."
                  />
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={closePaymentModal}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200"
                    disabled={recordingPayment}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50"
                    disabled={recordingPayment}
                  >
                    {recordingPayment ? 'Recording...' : 'Record Payment'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Invoice View Modal */}
      {showInvoiceModal && selectedSupplierForInvoices && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-11/12 max-w-7xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Invoice Details - {selectedSupplierForInvoices.supplierName}
                </h3>
                <button
                  onClick={closeInvoiceModal}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {loadingInvoices ? (
                <div className="flex justify-center items-center h-64">
                  <LoadingSpinner size="lg" />
                </div>
              ) : supplierInvoices ? (
                <div className="space-y-4">
                  {/* Supplier Summary */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="grid grid-cols-2 md:grid-cols-6 gap-4 text-sm">
                      <div>
                        <span className="text-gray-600">Total Invoices:</span>
                        <span className="ml-2 font-medium">{supplierInvoices.summary.totalInvoices}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Total Amount:</span>
                        <span className="ml-2 font-medium">{formatCurrency(supplierInvoices.summary.totalInvoiceAmount)}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Total Tax:</span>
                        <span className="ml-2 font-medium">{formatCurrency(supplierInvoices.summary.totalTaxAmount)}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Total Paid:</span>
                        <span className="ml-2 font-medium">{formatCurrency(supplierInvoices.summary.totalPaidAmount)}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Total Balance:</span>
                        <span className="ml-2 font-medium">{formatCurrency(supplierInvoices.summary.totalBalanceAmount)}</span>
                      </div>
                      <div>
                        <span className="text-gray-600">Payment %:</span>
                        <span className="ml-2 font-medium">{supplierInvoices.summary.averagePaymentPercentage.toFixed(1)}%</span>
                      </div>
                    </div>
                  </div>

                  {/* Invoices Table */}
                  <div className="bg-white rounded-lg shadow overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Invoice</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Payment Due On</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount (inc. tax)</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Paid</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Remaining Amount</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Payment Amount</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {supplierInvoices.invoices.map((invoice, index) => (
                            <tr key={invoice.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                              <td className="px-4 py-4 whitespace-nowrap">
                                <div className="flex items-center">
                                  <div className="flex-shrink-0 h-8 w-8">
                                    <div className="h-8 w-8 rounded bg-green-100 flex items-center justify-center">
                                      <svg className="h-5 w-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                      </svg>
                                    </div>
                                  </div>
                                  <div className="ml-3">
                                    <div className="text-sm font-medium text-gray-900">{invoice.invoiceNumber}</div>
                                  </div>
                                </div>
                              </td>
                              <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                                {new Date(invoice.invoiceDate).toLocaleDateString()}
                              </td>
                              <td className="px-4 py-4 whitespace-nowrap">
                                <div className="flex items-center">
                                  <span className="text-sm text-gray-900">
                                    {invoice.dueDate ? new Date(invoice.dueDate).toLocaleDateString() : 'N/A'}
                                  </span>
                                  {invoice.dueDate && new Date(invoice.dueDate) < new Date() && invoice.balanceAmount > 0 && (
                                    <svg className="ml-2 h-4 w-4 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                    </svg>
                                  )}
                                </div>
                              </td>
                              <td className="px-4 py-4 whitespace-nowrap text-sm text-green-600 font-medium">
                                {formatCurrency(invoice.totalWithTax)}
                              </td>
                              <td className="px-4 py-4 whitespace-nowrap text-sm text-green-600 font-medium">
                                {formatCurrency(invoice.paidAmount)}
                              </td>
                              <td className="px-4 py-4 whitespace-nowrap text-sm text-green-600 font-medium">
                                {formatCurrency(invoice.balanceAmount)}
                              </td>
                              <td className="px-4 py-4 whitespace-nowrap">
                                {invoice.balanceAmount > 0 ? (
                                  <div className="flex items-center space-x-2">
                                    <input
                                      type="number"
                                      step="0.01"
                                      min="0"
                                      max={invoice.balanceAmount}
                                      value={paymentAmounts[invoice.id] || ''}
                                      onChange={(e) => handleInlinePaymentChange(invoice.id, e.target.value)}
                                      className="w-20 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                      placeholder="0.00"
                                    />
                                    <select
                                      className="w-20 px-2 py-1 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                      value={paymentMethods[invoice.id] || 'bank_transfer'}
                                      onChange={(e) => handlePaymentMethodChange(invoice.id, e.target.value)}
                                    >
                                      <option value="bank_transfer">Bank</option>
                                      <option value="cash">Cash</option>
                                      <option value="cheque">Cheque</option>
                                      <option value="online">Online</option>
                                    </select>
                                    <button
                                      onClick={() => handleInlinePaymentSubmit(invoice)}
                                      disabled={recordingPayments[invoice.id] || !paymentAmounts[invoice.id] || parseFloat(paymentAmounts[invoice.id] || 0) <= 0}
                                      className="px-2 py-1 text-xs font-medium text-white bg-indigo-600 rounded hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                      title="Record Payment"
                                    >
                                      {recordingPayments[invoice.id] ? (
                                        <svg className="animate-spin h-3 w-3" fill="none" viewBox="0 0 24 24">
                                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                      ) : (
                                        <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                        </svg>
                                      )}
                                    </button>
                                  </div>
                                ) : (
                                  <span className="text-sm text-gray-500">Fully Paid</span>
                                )}
                              </td>
                              <td className="px-4 py-4 whitespace-nowrap">
                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${invoice.paymentPercentage >= 90
                                    ? 'bg-green-100 text-green-800'
                                    : invoice.paymentPercentage >= 70
                                      ? 'bg-yellow-100 text-yellow-800'
                                      : 'bg-red-100 text-red-800'
                                  }`}>
                                  {invoice.paymentPercentage >= 90 ? 'Paid' : invoice.paymentPercentage >= 70 ? 'Partial' : 'Pending'}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-500">No invoice data available</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BusinessIntelligence;

